import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { CertificatesContext } from '../../contexts/certificates.context';

import './certificates.style.scss';

/**
 * Кнопка сертифіката в рядку курсу.
 *
 * Іконка вбудована, а не зі спрайту: у `img/sprites.svg` шапки випускника
 * немає, а правити спільний спрайт заради однієї іконки — зайвий ризик для
 * решти кабінету.
 *
 * ❗Стилі імпортуються ТУТ, а не через `scss/app.scss`. У кабінеті так робить
 * кожен компонент зі своїм оформленням — і головне, ручний рядок в `app.scss`
 * легко пропустити при накаті: тоді кнопка потрапляє в DOM без розмірів
 * і сторінка виглядає так, ніби фічі немає взагалі.
 *
 * ❗Рендериться ЗАВЖДИ, навіть коли CRM не дала стану по цій програмі.
 * Раніше в такому випадку не показувалось нічого — і «бекенд не відповів»
 * виглядало точно так само, як «у цього курсу немає сертифікатів». Тепер
 * кнопка сіра, а причину видно в модалці.
 */
const CertificateButton = ({ programId, programTitle, variant }) => {
  const { byProgram, setOpenProgram } = useContext(CertificatesContext);
  const item = byProgram[programId] ?? null;

  const state = item?.issued ? 'is-done' : item?.can_issue ? 'is-ready' : 'is-wait';
  const title = item?.issued
    ? 'Сертифікат видано'
    : item?.can_issue
      ? 'Сертифікат можна отримати'
      : 'Умови отримання сертифіката';

  const button = (
    <button
      type="button"
      className={`certificate-btn ${state}`}
      data-certificate-btn={programId}
      title={title}
      aria-label={title}
      onClick={() => setOpenProgram({ id: programId, title: programTitle })}
    >
      {/* `icon icon--md` — глобальні класи кабінету: навіть якщо локальний
          SCSS не підвантажиться, svg матиме розмір і кнопку буде видно. */}
      <svg className="icon icon--md certificate-btn__icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 3 1.5 8.25 12 13.5l8.25-4.125V15h1.5V8.25L12 3Z"
          fill="currentColor"
        />
        <path
          d="M5.25 11.06v3.69c0 .6.3 1.16.81 1.48C7.5 17.16 9.66 18 12 18s4.5-.84 5.94-1.77c.5-.32.81-.88.81-1.48v-3.69L12 14.87l-6.75-3.81Z"
          fill="currentColor"
        />
      </svg>
      {item?.can_issue && <span className="certificate-btn__dot" aria-hidden="true" />}
    </button>
  );

  // Варіант для мобільної картки: підпис рендериться тут, а не в картці, —
  // щоб картка не знала про внутрішню розмітку кнопки.
  if (variant === 'card') {
    return (
      <div className="card-program-block card-program-block__cert">
        <div className="card-program-block__title">Сертифікат</div>
        {button}
      </div>
    );
  }

  return button;
};

CertificateButton.propTypes = {
  programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  programTitle: PropTypes.string,
  variant: PropTypes.oneOf(['icon', 'card']),
};

CertificateButton.defaultProps = {
  programTitle: '',
  variant: 'icon',
};

export default CertificateButton;
